import React, {useCallback, useContext, useMemo} from "react";
import {Input, Rate} from "antd";
import {StyledButton} from "../components/StyledButton";
import {CheckOutlined} from "@ant-design/icons";
import classnames from "classnames";
import {RATING_LABELS, FEEDBACK_OPTIONS, ISSUE_OPTIONS, RATING_KEYS} from "./consts.jsx";
import {OrderFeedbackContext} from "./OrderFeedbackProvider";
import "./order-feedback.css";

const {TextArea} = Input;

export const OrderFeedbackRatingPage = ({history, match}) => {
    const {orderId} = match?.params || {};
    const {order, formValues, onFormValueChange} = useContext(OrderFeedbackContext);
    const {rating, selectedOptions, comment} = formValues;

    const isDisabled = !!order?.rating;

    const onClickRatingOption = useCallback(
        optionId => {
            if (isDisabled) return;
            const currentOptions = formValues.selectedOptions || [];
            const newOptions = currentOptions.includes(optionId)
                ? currentOptions.filter(id => id !== optionId)
                : [...currentOptions, optionId];
            onFormValueChange("selectedOptions", newOptions);
        },
        [formValues.selectedOptions, onFormValueChange, isDisabled]
    );

    const onChangeRating = useCallback(
        value => {
            if (isDisabled) return;
            onFormValueChange("rating", value);
            onFormValueChange("selectedOptions", []);
            onFormValueChange("comment", "");
        },
        [onFormValueChange, isDisabled]
    );

    const isFormValid = useMemo(() => rating > 0 && selectedOptions.length > 0, [rating, selectedOptions, comment]);

    return (
        <>
            <div className="order-feedback-section">
                <h2 className="order-feedback-section-title">מה הדירוג שלך להזמנה?</h2>
                <p className="order-feedback-section-description">
                    הדירוג יופיע באתר, יעזור לקולגות לבחור, לספקים להשתפר ולנו לשמור על רמת איכות גבוהה.
                </p>

                <div className="order-feedback-rating-container">
                    <div className="order-feedback-stars-wrapper">
                        <Rate
                            value={rating}
                            onChange={onChangeRating}
                            disabled={isDisabled}
                            className="order-feedback-stars"
                            style={{fontSize: 40, direction: "rtl"}}
                        />
                        <div className="order-feedback-stars-labels">
                            <span
                                className={classnames("order-feedback-star-label", "order-feedback-star-label-first", {
                                    "order-feedback-star-label-selected": rating === RATING_KEYS.TERRIBLE
                                })}>
                                {RATING_LABELS[RATING_KEYS.TERRIBLE]}
                            </span>
                            <span
                                className={classnames("order-feedback-star-label", "order-feedback-star-label-spacer", {
                                    "order-feedback-star-label-visible": rating === RATING_KEYS.BAD
                                })}>
                                {rating === RATING_KEYS.BAD ? RATING_LABELS[RATING_KEYS.BAD] : ""}
                            </span>
                            <span
                                className={classnames("order-feedback-star-label", "order-feedback-star-label-spacer", {
                                    "order-feedback-star-label-visible": rating === RATING_KEYS.OK
                                })}>
                                {rating === RATING_KEYS.OK ? RATING_LABELS[RATING_KEYS.OK] : ""}
                            </span>
                            <span
                                className={classnames("order-feedback-star-label", "order-feedback-star-label-spacer", {
                                    "order-feedback-star-label-visible": rating === RATING_KEYS.GOOD
                                })}>
                                {rating === RATING_KEYS.GOOD ? RATING_LABELS[RATING_KEYS.GOOD] : ""}
                            </span>
                            <span
                                className={classnames("order-feedback-star-label", "order-feedback-star-label-last", {
                                    "order-feedback-star-label-selected": rating === RATING_KEYS.EXCELLENT
                                })}>
                                {RATING_LABELS[RATING_KEYS.EXCELLENT]}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {rating > 0 && (
                <>
                    <div className="order-feedback-section">
                        <h3 className="order-feedback-section-subtitle">
                            {rating >= RATING_KEYS.OK
                                ? "כיף לשמוע! נשמח לדעת מה גרם לך לדרג כך"
                                : "נשמח להבין מה השתבש"}
                        </h3>
                        {!!(rating < RATING_KEYS.OK) && (
                            <p className="order-feedback-section-description">מה לא עבד כמו שציפית?</p>
                        )}
                        <div className="order-feedback-options-container">
                            {(rating >= RATING_KEYS.OK ? FEEDBACK_OPTIONS : ISSUE_OPTIONS).map(option => {
                                const isSelected = selectedOptions.includes(option.id);
                                return (
                                    <button
                                        key={option.id}
                                        type="button"
                                        className={`order-feedback-option ${isSelected ? "selected" : ""}`}
                                        onClick={() => onClickRatingOption(option.id)}
                                        disabled={isDisabled}>
                                        <span>{option.label}</span>
                                        {isSelected && <CheckOutlined className="order-feedback-check-icon" />}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className="order-feedback-section">
                        <TextArea
                            value={comment}
                            onChange={e => {
                                if (!isDisabled) {
                                    onFormValueChange("comment", e.target.value);
                                }
                            }}
                            disabled={isDisabled}
                            placeholder={
                                rating <= RATING_KEYS.BAD && rating > 0
                                    ? "ספרי לנו כדי שנוכל לתקן"
                                    : "נשמח לדעת מה אהבת ומה ניתן לשפר לפעם הבאה"
                            }
                            rows={4}
                            className="order-feedback-comment"
                            style={{direction: "rtl", textAlign: "right"}}
                        />
                    </div>
                </>
            )}

            <div className="order-feedback-actions">
                <StyledButton
                    onClick={() => history.push(`/order/${orderId}/feedback/upload`)}
                    disabled={!isFormValid}
                    className={classnames("order-feedback-button", {disabled: !isFormValid})}>
                    המשך
                </StyledButton>
            </div>
        </>
    );
};
